/**
 * CSAT campaign email links: /survey/login?survey_id=…&csat_token=…
 *
 * The token says which email the customer is answering. The server uses it
 * to attribute the response and to stop a second submission from the same
 * email (409), and it refuses an unknown or foreign link (410).
 *
 * Kept in sessionStorage, not localStorage or redux: Login and the thank-you
 * page clear localStorage, redux-persist writes the survey state to
 * localStorage, and tabs share both — one customer can have several emails
 * (one per purchase) open at once. Stored with its survey id and only
 * attached while that survey is the one being answered. Not cleared on the
 * thank-you page: going Back and submitting again must still hit the
 * server's one-answer-per-email check (409), not slip through unattributed.
 */

const STORAGE_KEY = "sf_csat_token";

export const CSAT_ERROR_CODES = Object.freeze({
  ALREADY_SUBMITTED: "CSAT_ALREADY_SUBMITTED",
  TOKEN_INVALID: "CSAT_TOKEN_INVALID",
});

/** Remember the link's token (Login). A link without one clears it. */
export const rememberCsatToken = (token, surveyId) => {
  try {
    if (token && surveyId) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ token: String(token), survey_id: String(surveyId) }),
      );
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
};

/** The token for `surveyId` (default: the survey being answered), else null. */
export const getCsatToken = (surveyId = localStorage.getItem("survey")) => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    if (stored?.token && surveyId && stored.survey_id === String(surveyId)) {
      return stored.token;
    }
  } catch {
    // Unreadable storage or a malformed entry — no token.
  }
  return null;
};

/** `metaData` with the token added when this survey has one (mutates and returns it). */
export const withCsatToken = (metaData, surveyId) => {
  const token = getCsatToken(surveyId ?? metaData?.survey_id);
  if (token && metaData) {
    metaData.csat_token = token;
  }
  return metaData;
};

/**
 * The CSAT error code of a failed request: an axios error (submit, partial
 * save) or a postData JSON body (getSurvey). Null for anything else.
 */
export const csatErrorCode = (errorOrBody) => {
  const code = errorOrBody?.response?.data?.code ?? errorOrBody?.code;
  return Object.values(CSAT_ERROR_CODES).includes(code) ? code : null;
};
